import anime from 'animejs'
import chunk from 'lodash/chunk'
import interact from 'interactjs'
import lottie from 'lottie-web'

const logID = 'debug.farmvibes'
const frames = 107

//

const SELECTORS = {
  element: '.js-farmvibes-drone'
}

class FarmVibesDrone {
  constructor (element) {
    this.element = element

    //

    this.assetsDirectory = this.element.dataset.assetsDirectory

    //

    this.height = this.element.offsetHeight
    this.width = this.element.offsetWidth

    this.breakpoint = 'desktop'
    if (this.width < 768) {
      this.breakpoint = 'mobile'
    }

    //

    this.cache = {
      desktop: new Array(frames + 1).fill(null),
      mobile: new Array(frames + 1).fill(null)
    }

    //

    this.inFocus = false

    this.position = { x: 0, y: 0 }
    this.minX = 0
    this.maxX = 0

    this.init()
  }

  //

  async init() {
    // console.log(logID, 'init')

    await this.initDOM()
    await this.initLotties()
    await this.initReset()
    await this.initKeyboard()
    await this.initResizeObserver()
  }

  async initDOM() {
    // console.log(logID, 'initDOM')

    this.draggable = document.querySelector('.farmvibes-drone-draggable ._interactive')
    this.assetBounds = document.querySelector('.farmvibes-drone-draggable ._bounds')
    this.propeller = document.querySelectorAll('.farmvibes-drone-draggable ._propeller')
    this.handle = document.querySelector('.farmvibes-drone-draggable ._handle')
    this.handleProgress = document.querySelector('.farmvibes-drone-draggable ._handle progress')

    this.canvas = document.querySelector('.farmvibes-drone-mask canvas')
    this.ctx = this.canvas.getContext('2d')

    this.track = document.querySelector('.farmvibes-drone-track')

    this.status = document.querySelector('.farmvibes-drone-status')

    this.reset = document.querySelector('.farmvibes-drone-reset')
    this.resetButton = this.reset.querySelector('button')

    this.alert = document.querySelector('.farmvibes-drone-alert')
  }

  async initLotties() {
    // console.log(logID, 'initLotties')

    this.scanCompleteLottie = document.querySelector('.farmvibes-drone-status')

    this.scanComplete = lottie.loadAnimation({
      autoplay: false,
      container: this.scanCompleteLottie,
      loop: false,
      path: `${this.assetsDirectory}/lottie/scan-complete.json`,
      renderer: 'svg'
    })
  }

  async initReset() {
    // console.log(logID, 'initReset')

    const handle = () => {
      // console.log(logID, 'initReset', 'handle')

      const self = this

      anime({
        targets: this.position,
        x: 0,
        easing: 'easeInOutCubic',
        update: function() {
          self.updatePosition()
        }
      })

      this.alert.innerHTML = ''
    }

    this.resetButton.addEventListener('click', handle.bind(this))
  }

  async initKeyboard() {
    // console.log(logID, 'initKeyboard')

    const handle = (event) => {
      // console.log(logID, 'initKeyboard', 'handle', this.inFocus)

      if (!this.inFocus) return

      // console.log(logID, 'event', event)
      const { key, code } = event
      // console.log(logID, 'event', { key, code })

      // console.log(logID, 'debug.position.a', this.position)

      let direction

      if (['ArrowLeft', 'ArrowDown'].includes(key)) {
        direction = -1
      } else if (['ArrowRight', 'ArrowUp'].includes(key)) {
        direction = 1
      } else {
        return
      }

      this.position.x += 2 * direction

      // console.log(logID, 'event', this.position)
      if (this.position.x < 0) this.position.x = 0
      if (this.position.x > this.track.offsetWidth) this.position.x = this.track.offsetWidth
      // console.log(logID, 'event', this.position)

      let copy = ''

      if (direction === 1 && this.position.x > 5) {
        copy = 'The drone is starting to move across the screen: during a 30 to 40 minute flight across fields, it captures images a farmer can get data through.'
      }

      if (direction === 1 && (this.position.x > this.track.offsetWidth / 2)) {
        copy = 'At the halfway point, the drone continues to capture the farm land below, which allows a farmer to see every part of a 300 acre field in precise detail.'
      }

      if (direction === 1 && (this.position.x > this.track.offsetWidth - 5)) {
        copy = 'The drone is completing its scan across the screen: by the end of its flight, it has captured 15 to 20 gigabytes of data, which will be stitched together to create a clear map of the entire farm.'
      }

      this.alert.innerHTML = copy

      // console.log(logID, 'debug.position.b', this.position)

      this.updatePosition()
    }

    document.removeEventListener('keydown', handle)
    document.addEventListener('keydown', handle.bind(this))

    //

    const focus = () => {
      // console.log(logID, 'initKeyboard', 'focus')
      this.inFocus = true
    }

    this.handleProgress.removeEventListener('focus', focus)
    this.handleProgress.addEventListener('focus', focus.bind(this))

    //

    const blur = () => {
      // console.log(logID, 'initKeyboard', 'blur')
      this.inFocus = false
    }

    this.handleProgress.removeEventListener('blur', blur)
    this.handleProgress.addEventListener('blur', blur.bind(this))

    //

    const handleClickOutsideBox = (event) => {
      if (!this.element.contains(event.target)) {
        // console.log(logID, 'initKeyboard', 'focus', 'false')
        this.inFocus = false
      } else {
        // console.log(logID, 'initKeyboard', 'focus', 'true')
        this.inFocus = true
      }
    }

    document.addEventListener('click', handleClickOutsideBox)
  }

  async initResizeObserver() {
    // console.log(logID, 'initResizeObserver')

    const resizeObserver = new ResizeObserver((entries) => {
      const element = entries[0]
      const contentBoxSize = element.contentBoxSize[0]

      this.height = contentBoxSize.blockSize
      this.width = contentBoxSize.inlineSize

      this.update()
    })

    resizeObserver.observe(this.element)
  }

  //

  async update () {
    // console.log(logID, 'update')

    await this.updateBreakpoint()
    await this.updateImages()
    await this.updateDraggable()
  }

  async updateBreakpoint() {
    // console.log(logID, 'updateBreakpoint')

    this.breakpoint = 'desktop'
    if (this.width < 768) {
      this.breakpoint = 'mobile'
    }

    //

    if (!this.canvas) return

    if (this.breakpoint === 'desktop') {
      this.canvas.width = 1712
      this.canvas.height = 1372
    } else {
      this.canvas.width = 900
      this.canvas.height = 1200
    }
  }

  async updateImages() {
    // console.log(logID, 'updateImages')

    //

    const check = () => {
      const pending = this.cache[this.breakpoint]
        .map((item, index) => {
          if (index > 0 && item === null) return index
          return null
        })
        .filter((item) => item)

      const preloaded = this.cache[this.breakpoint]
        .map((item, index) => {
          if (item) return index
          return null
        })
        .filter((item) => item)

      // console.log(logID, 'debug.preload', { preloaded: preloaded.length, pending: pending.length })

      return {
        pending,
        preloaded
      }
    }

    const getSrc = (index) => {
      const base = `${this.assetsDirectory}/sequence/${this.breakpoint}`

      const image = `${index
        .toString()
        .padStart(3, '0')}.webp`

      return `${base}/${image}`
    }

    const preloadIndex = async (index) => {
      // console.log(logID, 'debug.preload', { index })

      const src = getSrc(index)

      const image = new Image()
      image.src = src

      await image.decode()

      this.cache[this.breakpoint][index] = image

      return image
    }

    const preloadIndexes = async(chunk) => {
      const promises = []
      for (const index of chunk) {
        if (this.cache[this.breakpoint][index]) {
          // console.log(logID, 'debug.preload', { index }, 'already preloaded')
          continue
        }
        promises.push(preloadIndex(index))
      }

      const responses = await Promise.allSettled(promises)

      const fulfilled = responses.filter((response) => response.status === 'fulfilled')
      const rejected = responses.filter((response) => response.status === 'rejected')
      // console.log(logID, 'debug.preload', { fulfilled: fulfilled.length, rejected: rejected.length })
    }

    const sleep = (milliseconds) => {
      return new Promise((resolve) => {
        setTimeout(() => {
          resolve('done')
        }, milliseconds)
      })
    }

    //

    const { pending } = check()

    const array = pending.slice(0, frames)
    const size = 30
    const chunks = chunk(array, size)

    // console.log(logID, 'debug.preload', { array: array.length, size, chunks: chunks.length })

    for (const chunk of chunks) {
      await sleep(100)
      await preloadIndexes(chunk)
      await sleep(100)
    }

    this.element.classList.remove('--loading')
  }

  async updateDraggable() {
    // console.log(logID, 'updateDraggable')

    //

    this.minX = 0
    this.maxX = this.track.offsetWidth

    this.updatePosition()

    const self = this

    //

    let instance = interact(this.draggable)

    instance.unset()

    instance = interact(this.draggable)

    instance.draggable({
      allowFrom: this.handle,
      intertia: false,
      listeners: {
        start (event) {
          // console.log(logID, 'debug.start', event.type, event.target)
        },
        move (event) {
          self.position.x += event.dx
          self.position.y += event.dy

          self.updatePosition()
        },
        end (event) {
          // console.log(logID, 'debug.end', event.type, event.target)
        }
      },
      lockAxis: 'x',
      modifiers: [
        interact.modifiers.snap({
          targets: [
            interact.snappers.grid({ x: 1, y: 1 })
          ],
          range: Infinity,
          relativePoints: [{ x: 0, y: 0 }]
        }),
        interact.modifiers.restrictRect({
          restriction: 'parent'
        })
      ]
    }).on('down up hold dragstart dragend', (event) => {
      // console.log(logID, 'debug.event', event.type)
      this.handleProgress.blur()
      if (['down', 'dragstart'].includes(event.type)) {
        this.assetBounds.classList.add('dragging')
      }
      if (['up', 'dragend'].includes(event.type)) {
        this.assetBounds.classList.remove('dragging')
      }
    })

    // console.log(logID, 'debug.interact', interact.isSet(this.draggable), instance.draggable())
  }

  //

  updatePosition() {
    // console.log(logID, 'debug.position.1', this.position, this.maxX)

    //

    const clipWidth1 = this.breakpoint === 'desktop' ? this.width * 3 / 7 : this.width * 2 / 4
    const clipWidth2 = clipWidth1 - this.position.x
    // console.log(logID, 'debug.position.1', clipWidth1, clipWidth2)
    document.querySelector('.farmvibes-drone-mask').style.clipPath = `inset(0 ${clipWidth2}px 0 0)`

    //

    if (this.position.x < 1) {
      this.position.x = 0
    }

    if (this.position.x > this.maxX) {
      this.position.x = this.maxX
    }

    this.position.x = Math.round(this.position.x)

    // console.log(logID, 'debug.position.2', this.position, this.maxX)

    //

    this.draggable.style.transform = `translate(${this.position.x}px, ${this.position.y}px)`

    //

    const percent0to1 = this.position.x / this.track.offsetWidth
    const percent1to100 = percent0to1 * 100

    // console.log(logID, 'debug.percent', { percent0to1, percent1to100 })

    this.handleProgress.value = percent1to100
    this.handleProgress.setAttribute('aria-valuenow', percent1to100)

    //

    const trackWidth = this.track.offsetWidth

    const progress = this.position.x / trackWidth
    const angle = progress * (360 * 2)

    // console.log(logID, 'debug.progress', this.position, { progress, trackWidth, angle })

    this.propeller.forEach(item => {
      item.style.transform = `rotate(${angle}deg)`
    })

    const frame = progress * frames

    const normalizeIndex = (index) => {
      return Math.min(Math.max(Math.floor(index), 1), frames)
    }

    const index = normalizeIndex(frame)

    // console.log(logID, 'debug.progress', { progress, frame, index })

    // const src = getSrc(index)
    // console.log(logID, 'debug.drone', src)
    // image.src = src

    try {
      // console.log(logID, 'debug.draw', cache[index])
      this.ctx.drawImage(this.cache[this.breakpoint][index], 0, 0)
    } catch (error) {
      console.error(logID, 'debug', { index, error })
    }

    // console.log(logID, 'debug', this.position, this.maxX)
    if (parseInt(this.position.x) > this.maxX - 5) {
      this.status.classList.remove('--hidden')
      this.scanComplete.play()

      this.reset.classList.remove('--hidden')
      this.reset.setAttribute('aria-hidden', 'false')
    } else {
      this.status.classList.add('--hidden')
      this.scanComplete.stop()

      this.reset.classList.add('--hidden')
      this.reset.setAttribute('aria-hidden', 'true')
    }
  }
}

export default {
  selector: SELECTORS.element,
  Source: FarmVibesDrone
}
